import { Grid, Typography } from '@mui/material';

import React from 'react';
import { AccountData, Currency, LocationsStatus } from '../api';
import { isBookingFeeOn } from './core/pricing/bookingFee';

type DeepOmitNull<T> = {
  [K in keyof T]: T[K] extends object ? DeepOmitNull<T[K]> : T[K];
};

export function salesTaxText(taxRate?: number, shouldNotSpecifyTax = false) {
  return shouldNotSpecifyTax ? ` + sales tax` : ` + ${taxRate}% sales tax`;
}

export function priceFormatterFromCurrency(
  price: number,
  currency: Currency,
  taxRate?: number,
  withTax = false,
  shouldNotSpecifyTax = false,
  fractionDigits = 0,
): JSX.Element | string {
  const localeMap: Record<string, string> = {
    ils: 'he-IL',
    eur: 'de-DE',
    usd: 'en-US',
  };
  const locale = localeMap[currency.toLowerCase()] || 'en-US';
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  const shouldRenderTax = taxRate && withTax;

  const formattedPrice = formatter.format(price);
  if (!shouldRenderTax) {
    return formattedPrice;
  }
  return (
    <Grid container gap={1} alignItems="center">
      <Typography>{formattedPrice}</Typography>
      <Typography fontSize={'12px'} fontWeight={300}>
        {salesTaxText(taxRate, shouldNotSpecifyTax)}
      </Typography>
    </Grid>
  );
}

export function deepOmitNull<T>(obj: T): DeepOmitNull<T> {
  if (!obj) {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value !== null)
      .map(([key, value]) => [key, value !== null && typeof value === 'object' ? deepOmitNull(value) : value]),
  ) as DeepOmitNull<T>;
}

export function shouldNotSpecifyTax(account: AccountData) {
  const hasStripeIntegration = !!account.stripe_connect;
  const hasMultipleLocations = account.locations.filter((x) => x.status === LocationsStatus.ACTIVE).length > 1;
  return hasStripeIntegration && hasMultipleLocations;
}

export function customerFacingPriceFormatter(price: number, account: AccountData, withTax = false) {
  return priceFormatterFromCurrency(
    price,
    account.currency,
    account.locations?.[0]?.stripe_tax_rate,
    withTax,
    shouldNotSpecifyTax(account),
    isBookingFeeOn(account) ? 2 : 0,
  );
}
